import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import "../i18n";
import AddPhotoAlternateRoundedIcon from "@mui/icons-material/AddPhotoAlternateRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";

import { getApiError } from "../api/errors";
import {
  deleteProductVariantImage,
  getProductImagePolicy,
  getProductVariantImage,
  uploadProductVariantImage,
} from "../api/products";

const FALLBACK_SUPPORTED_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

export default function VariantImageDialog({ open, product, variant, onClose, onChanged }) {
  const { t } = useTranslation();
  const [policy, setPolicy] = useState(null);
  const [remoteUrl, setRemoteUrl] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const localUrl = useMemo(
    () => (selectedFile ? URL.createObjectURL(selectedFile) : ""),
    [selectedFile],
  );

  useEffect(() => () => {
    if (localUrl) URL.revokeObjectURL(localUrl);
  }, [localUrl]);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    getProductImagePolicy()
      .then((response) => {
        if (active) setPolicy(response.data || null);
      })
      .catch(() => {
        if (active) setPolicy(null);
      });
    return () => { active = false; };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    setSelectedFile(null);
    setError("");
    setSuccess("");
    return undefined;
  }, [open, variant?.public_id]);

  useEffect(() => {
    if (!open || !product?.public_id || !variant?.public_id || !variant?.image) {
      setRemoteUrl("");
      setLoading(false);
      return undefined;
    }

    let active = true;
    let objectUrl = "";
    setLoading(true);
    setError("");
    getProductVariantImage(product.public_id, variant.public_id)
      .then((response) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(response.data);
        setRemoteUrl(objectUrl);
      })
      .catch((requestError) => {
        if (active) setError(getApiError(requestError, t("images.loadVariantError")));
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, product?.public_id, variant?.public_id, variant?.image, t]);

  const choose = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    const supportedTypes = new Set(
      Array.isArray(policy?.allowed_mime_types)
        ? policy.allowed_mime_types
        : [...FALLBACK_SUPPORTED_TYPES],
    );
    if (!supportedTypes.has(file.type)) {
      setError(t("images.invalidType"));
      return;
    }

    const maximumBytes = Number(policy?.max_bytes || 0);
    if (file.size < 1 || (maximumBytes > 0 && file.size > maximumBytes)) {
      const maximumMb = maximumBytes > 0
        ? `${(maximumBytes / 1_000_000).toFixed(1)} MB`
        : t("images.configuredLimit");
      setError(t("images.tooLargeVariant", { size: maximumMb }));
      return;
    }

    setError("");
    setSuccess("");
    setSelectedFile(file);
  };

  const save = async () => {
    if (!selectedFile || !product?.public_id || !variant?.public_id) return;
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await uploadProductVariantImage(product.public_id, variant.public_id, selectedFile);
      setSelectedFile(null);
      await onChanged?.();
      setSuccess(t("images.savedVariant"));
    } catch (requestError) {
      setError(getApiError(requestError, t("images.saveVariantError")));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!product?.public_id || !variant?.public_id || !variant?.image) return;
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await deleteProductVariantImage(product.public_id, variant.public_id);
      setRemoteUrl("");
      setSelectedFile(null);
      await onChanged?.();
      setSuccess(t("images.removedVariant"));
    } catch (requestError) {
      setError(getApiError(requestError, t("images.removeVariantError")));
    } finally {
      setSaving(false);
    }
  };

  const previewUrl = localUrl || remoteUrl;
  const hasVariantImage = Boolean(variant?.image);

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {t("images.variantTitle")}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          {product?.name}{variant?.label ? ` · ${variant.label}` : ""}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          <Alert severity="info">
            {t("images.variantHelp")}
          </Alert>

          <Box
            sx={{
              minHeight: 240,
              borderRadius: 3,
              border: "1px dashed #bfdbfe",
              bgcolor: "#f8fbff",
              display: "grid",
              placeItems: "center",
              overflow: "hidden",
            }}
          >
            {loading ? (
              <CircularProgress size={30} />
            ) : previewUrl ? (
              <Box
                component="img"
                src={previewUrl}
                alt={`${product?.name || t("common.product")} ${variant?.label || t("common.variant")}`}
                sx={{ width: "100%", height: 300, objectFit: "contain", bgcolor: "#fff" }}
              />
            ) : (
              <Stack alignItems="center" spacing={0.75} sx={{ p: 3, color: "text.secondary" }}>
                <ImageRoundedIcon sx={{ fontSize: 48, color: "primary.main" }} />
                <Typography fontWeight={850}>{t("images.noVariant")}</Typography>
                <Typography variant="caption" textAlign="center">
                  {t("images.variantFallback")}
                </Typography>
              </Stack>
            )}
          </Box>

          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">{success}</Alert>}

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button
              component="label"
              variant="outlined"
              startIcon={<AddPhotoAlternateRoundedIcon />}
              disabled={saving}
            >
              {t(hasVariantImage || selectedFile ? "images.replace" : "images.uploadVariant")}
              <input hidden type="file" accept="image/jpeg,image/png,image/webp" onChange={choose} />
            </Button>
            {selectedFile && (
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveRoundedIcon />}
                disabled={saving}
                onClick={save}
              >
                {t("images.saveVariant")}
              </Button>
            )}
            {hasVariantImage && !selectedFile && (
              <Button
                color="error"
                variant="text"
                startIcon={<DeleteOutlineRoundedIcon />}
                disabled={saving}
                onClick={remove}
              >
                {t("images.removeOverride")}
              </Button>
            )}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>{t("common.close")}</Button>
      </DialogActions>
    </Dialog>
  );
}
